import { useEffect, useRef, useState } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import {
  addPendingEventListener,
  removeQueuedEventListener,
  addQueuedEventListener,
  QueuedEventListener,
  removePendingEventListener,
  setListenerContext,
  selectConnected,
  selectSessionID,
  selectCurrentEventListeners,
  selectQueuedEventListeners,
} from "../services/redux/shuteSlice";
import { RootState } from "../services/redux/store";
import { shuteSliceActions } from "../services/redux/actions/interfaces";
import { useAppDispatch, useAppSelector } from '../services/redux/hooks';


interface UseEventNotifierProps {
  psPage: string;
  ptWatchEvents: string[];
  shouldExecute: boolean;
  psPageSubContext: string;
  // ptPageContexts: Record<string, string[]>;
  psAction: 'add' | 'remove';
  ptCurrentEventListeners: any;
  dispatch: ThunkDispatch<RootState, any, shuteSliceActions>;
  sSputnikSessionID: string; // Assuming the type is string, update as necessary
  tPendingEventListeners: string[];
  tQueuedEventListeners: QueuedEventListener[]; // Assuming QueuedEventListener is the correct type
  isEventNotInQueuedListeners: boolean;
}
// Move the registerListeners function outside of the useEventNotifier function.
const registerListeners = async ({ 
  psPage
  ,ptWatchEvents
  ,shouldExecute
  ,psPageSubContext
  // ,ptPageContexts
  ,psAction
  ,ptCurrentEventListeners
  ,dispatch
  ,sSputnikSessionID
  ,tPendingEventListeners
  ,tQueuedEventListeners
  ,isEventNotInQueuedListeners
}: UseEventNotifierProps): Promise<boolean> => {
  
  // const isEventNotInQueuedListeners = ptWatchEvents.some(
  //   (event) => 
  //     tQueuedEventListeners.every(
  //       (listenerObj) => !listenerObj.watchevents.includes(event)
  //     )
  // );
  // console.log('yerp',tQueuedEventListeners.length);
  try {

    if (psAction=='add') {
      if (ptWatchEvents.some(item => !ptCurrentEventListeners.includes(item)) 
      && ptWatchEvents.some(item => !tPendingEventListeners.includes(item)) 
      && ptWatchEvents.some((event) => tQueuedEventListeners.every((listenerObj) => !listenerObj.watchevents.includes(event)))
      ) {
        // console.log(`dispatch(addQueuedEventListener({page:${psPage},watchevents:${ptWatchEvents},action:'add',sessionid:${sSputnikSessionID}}));`);
        dispatch(addQueuedEventListener({page:psPage,watchevents:ptWatchEvents,action:'add',sessionid:sSputnikSessionID,psPageSubContext:psPageSubContext}));
        return true;
    } 
  } else {
    if (ptWatchEvents.some(item => !ptCurrentEventListeners.includes(item))) 
      {
        // console.log(`dispatch(addQueuedEventListener({page:${psPage},watchevents:${ptWatchEvents},action:'${psAction}',sessionid:${sSputnikSessionID}}));`);
        dispatch(addQueuedEventListener({page:psPage,watchevents:ptWatchEvents,action:'remove',sessionid:sSputnikSessionID,psPageSubContext:psPageSubContext}));
        return true;
      } else if (ptWatchEvents.some(item => !tPendingEventListeners.includes(item)) 
          || isEventNotInQueuedListeners) 
      {
          // console.log(`dispatch(addQueuedEventListener({page:${psPage},watchevents:${ptWatchEvents},action:'add',sessionid:${sSputnikSessionID}}));`);
          // console.log(`dispatch(removeQueuedEventListener({page:${psPage},watchevents:${ptWatchEvents},action:'${psAction}',sessionid:${sSputnikSessionID}}));`);
          dispatch(removeQueuedEventListener({page:psPage,watchevents:ptWatchEvents,action:'add',sessionid:sSputnikSessionID,psPageSubContext:psPageSubContext}));
          return true;      
      }
    };
    return false;
  } catch (error) {
    console.error(error);
    return false
  }
};
export const useEventNotifier = ({ 
      psPage
      ,ptWatchEvents
      ,shouldExecute
      ,psPageSubContext
      // ,ptPageContexts
      ,psAction
      ,ptCurrentEventListeners
      ,dispatch
      ,sSputnikSessionID
      ,tPendingEventListeners
      ,tQueuedEventListeners
      ,isEventNotInQueuedListeners
    }: UseEventNotifierProps): boolean => {
  try {
    // console.log(shouldExecute,ptWatchEvents)
    if (!shouldExecute) return false; 

    const outcome = registerListeners({ 
      psPage
      ,ptWatchEvents
      ,shouldExecute
      ,psPageSubContext
      // ,ptPageContexts
      ,psAction
      ,ptCurrentEventListeners
      ,dispatch
      ,sSputnikSessionID
      ,tPendingEventListeners
      ,tQueuedEventListeners
      ,isEventNotInQueuedListeners
    }
    );

    return true;

    // return outcome;
  } catch (oError) {
    console.error(oError);
    return false;
  }
};

/**
 * Notifies registered event listeners about an event on a page.
 *
 * Moved here from services/sputnikapi.ts — its logic never actually called
 * the sputnik API directly, only dispatched Redux queue actions via
 * useEventNotifier above. Queue-draining (the real Atoll `events/register`
 * PUT) is handled entirely by each host app's own mounted Atoll middleware
 * (shared/atoll/redux/shuteCallbackRegistry.ts's combinedMiddleware) — this
 * hook only manages Redux queue state, by design.
 *
 * @param {string} psPage - The name of the page on which the event occurred.
 * @param {Object} ptWatchEvents - An object containing information about the event(s) being watched.
 * @param {string} psWatchEventAction - The action to take when the event is watched (defaults to 'add').
 * @returns {void}
 */
export function useConnectAndSetNotifyEventListers(
  psPage?: string,
  ptWatchEvents?: string[],
  psWatchEventAction: ('add'|'remove') = 'add',
  dispatch?: any,
  psPageSubContext?:string,
  ptPageEvents?:any
): boolean {
  if (!(psPage && ptWatchEvents && dispatch)) {
    return true;
  }
  const [isComplete, setIsComplete] = useState(false);
  const isCompleteRef = useRef(false);

  const isConnected = useAppSelector(selectConnected);
  const sSputnikSessionID = useAppSelector(selectSessionID);
  const tCurrentEventListeners = useAppSelector(selectCurrentEventListeners);
  const tQueuedEventListeners = useAppSelector(selectQueuedEventListeners);

  let tPendingEventListeners: string[] = [];
  const isEventNotInQueuedListeners = ptWatchEvents.some(event =>
    tQueuedEventListeners.every(listenerObj => !listenerObj.watchevents.includes(event))
  );

  useEffect(() => {
    if (!psPage || !ptWatchEvents || !dispatch) return;

    const newEventListeners = ptWatchEvents.filter(item => !tCurrentEventListeners.includes(item));

    if (newEventListeners.length === 0) {
      setIsComplete(true);
      isCompleteRef.current = true;
      return;
    }

    const registerListenersEffect = async () => {
      if (isConnected) {
        try {
          const notifierOutcome = useEventNotifier({
              psPage,
              ptWatchEvents: newEventListeners,
              shouldExecute: true,
              psPageSubContext: psPageSubContext||'',
              // ptPageContexts: ptPageEvents,
              psAction: psWatchEventAction,
              ptCurrentEventListeners:tCurrentEventListeners,
              dispatch:dispatch,
              sSputnikSessionID:sSputnikSessionID,
              tPendingEventListeners:tPendingEventListeners,
              tQueuedEventListeners:tQueuedEventListeners,
              isEventNotInQueuedListeners:isEventNotInQueuedListeners
          });

          isCompleteRef.current = notifierOutcome;
          setIsComplete(notifierOutcome);
        } catch (error) {
          isCompleteRef.current = true;
          setIsComplete(true);
        }
      }
    };
    if (psWatchEventAction=='add' && (!newEventListeners || newEventListeners.length === 0)) {
      // console.log(`ptWatchEvents: ${ptWatchEvents} already listening`)
      return
    }
    if (psWatchEventAction=='remove' && !(newEventListeners && newEventListeners.length !== 0)) {
      console.log(`ptWatchEvents: ${ptWatchEvents} not already listening`)
      return
    }
    registerListenersEffect();
  }, [psPage, ptWatchEvents, psWatchEventAction, tCurrentEventListeners, isConnected]);

  return isCompleteRef.current;
}